import React, { useLayoutEffect, useRef } from "react";
import {
  Database,
  UserPlus,
  Calendar,
  FileText,
  Settings,
  Folder,
  CheckCircle,
  GitBranch,
  Briefcase,
  TrendingUp,
  LogOut
} from "lucide-react";
import Navbar from "../../Components/Navbar";
import Call from "../../Components/Call";
import ChatbotWidget from "../ChatComponent";
import Footer from "../../Components/Footer";
import "../../Styling/Landing_Pages_css/HR.css";
import "../../Styling/Responsive.css";
import HeroLandingPage from "../../Components/LandingPagesComponents/Hero_Landing_Page";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" },
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
  viewport: { once: true },
};

export default function HRLandingPage() {
  const canvasRef = useRef(null);

  useLayoutEffect(() => {
    // 90-Degree Circuit Animation
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width, height, lines = [];

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", resize);
    resize();

    class CircuitLine {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.history = [{ x: this.x, y: this.y }];
        this.angle = (Math.floor(Math.random() * 4) * Math.PI) / 2;
        this.speed = 3; // Faster
        this.length = 0;
        this.maxLength = 50 + Math.random() * 150;
        this.opacity = 0.4 + Math.random() * 0.3; // Higher opacity
        this.color = Math.random() > 0.5 ? "#5b4cff" : "#00bfff";
      }

      update() {
        this.x += Math.cos(this.angle) * this.speed;
        this.y += Math.sin(this.angle) * this.speed;
        this.length += this.speed;

        if (this.length > this.maxLength) {
          this.angle += Math.random() > 0.5 ? Math.PI / 2 : -Math.PI / 2;
          this.length = 0;
          this.history.push({ x: this.x, y: this.y });
          if (this.history.length > 5) this.history.shift();
        }

        if (this.x < 0 || this.x > width || this.y < 0 || this.y > height) {
          this.reset();
        }
      }

      draw() {
        ctx.beginPath();
        ctx.strokeStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.lineWidth = 1.5; // Thicker lines
        ctx.moveTo(this.history[0].x, this.history[0].y);
        for (let i = 1; i < this.history.length; i++) {
          ctx.lineTo(this.history[i].x, this.history[i].y);
        }
        ctx.lineTo(this.x, this.y);
        ctx.stroke();
      }
    }

    for (let i = 0; i < 20; i++) lines.push(new CircuitLine());

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      lines.forEach(line => {
        line.update();
        line.draw();
      });
      requestAnimationFrame(animate);
    };

    animate();

    // Scroll GSAP
    const sections = gsap.utils.toArray('section');
    sections.forEach(section => {
      gsap.fromTo(section,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: section,
            start: "top 85%"
          }
        }
      );
    });

    return () => window.removeEventListener("resize", resize);
  }, []);
  const slides = [
    {
      image: "/images/Landing_page_images/hr/h_1.png",
      heading: "HR Shouldn’t Be Fragmented. It Should Be a System.",
      subheading:
        "Move away from spreadsheets, email threads and manual tools with a unified HR operating system.",
    },
    {
      image: "/images/Landing_page_images/hr/hr_2.png",
      heading: "Build Your Dream Team.",
      subheading: "Hire, onboard, and manage your employees with ease.",
    },
    {
      image: "/images/Landing_page_images/hr/hr_3.png",
      heading: "Secure and Compliant.",
      subheading:
        "Keep your employee data safe and stay compliant with local regulations.",
    },
  ];

  return (
    <>
      <Navbar></Navbar>

      {/* GSAP Animated Background */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: -1,
          pointerEvents: 'none',
          opacity: 0.7 // Bolder visibility
        }}
      />

      <HeroLandingPage
        slides={slides}
        badgeText="HR Operating System (HROS)"
        primaryBtn={{
          text: "Request Demo Access →",
          link: "https://studio.digitaldreamworksagency.com/access",
        }}

      />

      <section className="HR-complexity-section">
        <div className="container">
          <motion.h2
            className="HR-complexity-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            HR Complexity Shouldn't Require 15 Tools
          </motion.h2>
          <motion.p
            className="HR-complexity-desc"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Most teams cobble together HR operations from scattered tools,
            manual processes, and reactive workarounds. That creates chaos, not
            systems.
          </motion.p>

          <motion.div
            className="HR-complexity-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="HR-complexity-card" variants={fadeInUp}>
              <div className="HR-complexity-icon-wrapper">
                <Database className="HR-complexity-icon-svg" />
              </div>
              <p className="HR-complexity-text">
                Employee data scattered across spreadsheets, emails, and tools
              </p>
            </motion.div>
            <motion.div className="HR-complexity-card" variants={fadeInUp}>
              <div className="HR-complexity-icon-wrapper">
                <UserPlus className="HR-complexity-icon-svg" />
              </div>
              <p className="HR-complexity-text">
                Manual onboarding that takes days and constant follow-up
              </p>
            </motion.div>
            <motion.div className="HR-complexity-card" variants={fadeInUp}>
              <div className="HR-complexity-icon-wrapper">
                <Calendar className="HR-complexity-icon-svg" />
              </div>
              <p className="HR-complexity-text">
                Time-off requests tracked in Slack messages and calendars
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="HR-lifecycle-section">
        <div className="container">
          <motion.h2
            className="HR-lifecycle-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            The Employee Lifecycle as a Connected System
          </motion.h2>
          <motion.p
            className="HR-lifecycle-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            From hiring to offboarding, every stage connected in one intelligent
            workflow
          </motion.p>

          <motion.div
            className="HR-lifecycle-container"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="HR-lifecycle-step">
              <div className="HR-lifecycle-icon">
                <UserPlus size={24} />
              </div>
              <span className="HR-lifecycle-label">Onboarding</span>
            </div>
            <span className="HR-lifecycle-arrow">→</span>
            <div className="HR-lifecycle-step">
              <div className="HR-lifecycle-icon">
                <Briefcase size={24} />
              </div>
              <span className="HR-lifecycle-label">Day One</span>
            </div>
            <span className="HR-lifecycle-arrow">→</span>
            <div className="HR-lifecycle-step">
              <div className="HR-lifecycle-icon">
                <TrendingUp size={24} />
              </div>
              <span className="HR-lifecycle-label">Development</span>
            </div>
            <span className="HR-lifecycle-arrow">→</span>
            <div className="HR-lifecycle-step">
              <div className="HR-lifecycle-icon">
                <LogOut size={24} />
              </div>
              <span className="HR-lifecycle-label">Offboarding</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="HR-replaces-section">
        <div className="container">
          <motion.h2
            className="HR-replaces-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What HROS Replaces
          </motion.h2>

          <motion.div
            className="HR-replaces-container"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="HR-replace-item" variants={fadeInUp}>
              <div className="HR-replace-old">
                <span className="HR-replace-label">Before</span>
                <span className="HR-replace-text">
                  HR-recruitment-spreadsheets
                </span>
              </div>
              <div className="HR-replace-arrow">→</div>
              <div className="HR-replace-new">
                <span className="HR-replace-label">Now</span>
                <span className="HR-replace-text">One unified system</span>
              </div>
            </motion.div>

            <motion.div className="HR-replace-item" variants={fadeInUp}>
              <div className="HR-replace-old">
                <span className="HR-replace-label">Before</span>
                <span className="HR-replace-text">
                  Manual-onboarding-and-orientation
                </span>
              </div>
              <div className="HR-replace-arrow">→</div>
              <div className="HR-replace-new">
                <span className="HR-replace-label">Now</span>
                <span className="HR-replace-text">
                  Automated onboarding flows
                </span>
              </div>
            </motion.div>

            <motion.div className="HR-replace-item" variants={fadeInUp}>
              <div className="HR-replace-old">
                <span className="HR-replace-label">Before</span>
                <span className="HR-replace-text">
                  Email-scattered-approvals
                </span>
              </div>
              <div className="HR-replace-arrow">→</div>
              <div className="HR-replace-new">
                <span className="HR-replace-label">Now</span>
                <span className="HR-replace-text">
                  Workflow-all-user-processes
                </span>
              </div>
            </motion.div>

            <motion.div className="HR-replace-item" variants={fadeInUp}>
              <div className="HR-replace-old">
                <span className="HR-replace-label">Before</span>
                <span className="HR-replace-text">
                  Departmental-storage-databases
                </span>
              </div>
              <div className="HR-replace-arrow">→</div>
              <div className="HR-replace-new">
                <span className="HR-replace-label">Now</span>
                <span className="HR-replace-text">Central-all-HR-database</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="HR-modules-section">
        <div className="container">
          <motion.h2
            className="HR-modules-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Core System Modules
          </motion.h2>
          <motion.p
            className="HR-modules-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Everything your team needs in one unified platform
          </motion.p>

          <motion.div
            className="HR-modules-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="HR-module-card" variants={fadeInUp}>
              <div className="HR-module-icon">
                <FileText size={32} />
              </div>
              <h3 className="HR-module-title">Employee Records</h3>
              <p className="HR-module-desc">
                Centralized employee data, documents, and history in one system.
              </p>
            </motion.div>
            <motion.div className="HR-module-card" variants={fadeInUp}>
              <div className="HR-module-icon">
                <Settings size={32} />
              </div>
              <h3 className="HR-module-title">Onboarding Automation</h3>
              <p className="HR-module-desc">
                Automated workflows for new hire setup, training, and
                onboarding.
              </p>
            </motion.div>
            <motion.div className="HR-module-card" variants={fadeInUp}>
              <div className="HR-module-icon">
                <Calendar size={32} />
              </div>
              <h3 className="HR-module-title">Time & Leave Management</h3>
              <p className="HR-module-desc">
                Holistic approvals and time-off PTO days and schedules.
              </p>
            </motion.div>
            <motion.div className="HR-module-card" variants={fadeInUp}>
              <div className="HR-module-icon">
                <Folder size={32} />
              </div>
              <h3 className="HR-module-title">Document Management</h3>
              <p className="HR-module-desc">
                Store, organize, and retrieve employee documents and records.
              </p>
            </motion.div>
            <motion.div className="HR-module-card" variants={fadeInUp}>
              <div className="HR-module-icon">
                <CheckCircle size={32} />
              </div>
              <h3 className="HR-module-title">Compliance & Attestations</h3>
              <p className="HR-module-desc">
                Automated alerts for reviews, renewals, certifications and more.
              </p>
            </motion.div>
            <motion.div className="HR-module-card" variants={fadeInUp}>
              <div className="HR-module-icon">
                <GitBranch size={32} />
              </div>
              <h3 className="HR-module-title">Workflow Engine</h3>
              <p className="HR-module-desc">
                Custom approval flows for all workflows and HR team needs.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="HR-growing-section">
        <div className="container">
          <motion.h2
            className="HR-growing-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Built For Growing Teams
          </motion.h2>

          <motion.div
            className="HR-growing-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            <motion.div className="HR-growing-card" variants={fadeInUp}>
              <h3 className="HR-growing-card-title">10-50 Employee Teams</h3>
              <p className="HR-growing-card-desc">
                Organize spreadsheets, centralize workflows, eliminate manual
                chaos.
              </p>
            </motion.div>
            <motion.div className="HR-growing-card" variants={fadeInUp}>
              <h3 className="HR-growing-card-title">
                Agencies & Service-line Businesses
              </h3>
              <p className="HR-growing-card-desc">
                Manage teams across projects with complex workflows.
              </p>
            </motion.div>
            <motion.div className="HR-growing-card" variants={fadeInUp}>
              <h3 className="HR-growing-card-title">Ops-Heavy Organizations</h3>
              <p className="HR-growing-card-desc">
                Multiple roles, locations, and compliance requirements.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <div className="LD-divider"></div>
      <ChatbotWidget></ChatbotWidget>
      <Call></Call>
      <Footer></Footer>
    </>
  );
}

